import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  type Dispatch,
  type ReactElement,
  type ReactNode,
} from 'react'
import { PROOF_STEPS, type ProofStepId } from '../lib/proofSteps.js'

export interface InstallerState {
  captured: ProofStepId[]
  activeIdx: number
  anchorAttempted: boolean
  sealed: boolean
}

export type Action =
  | { type: 'START_PHOTO'; idx: number }
  | { type: 'MARK_CAPTURED'; id: ProofStepId }
  | { type: 'ANCHOR_ATTEMPTED' }
  | { type: 'SEAL' }
  | { type: 'RESET' }

export const initialState: InstallerState = {
  captured: [],
  activeIdx: 0,
  anchorAttempted: false,
  sealed: false,
}

export const reducer = (state: InstallerState, action: Action): InstallerState => {
  switch (action.type) {
    case 'START_PHOTO':
      return { ...state, activeIdx: action.idx }
    case 'MARK_CAPTURED':
      if (state.captured.includes(action.id)) return state
      return { ...state, captured: [...state.captured, action.id] }
    case 'ANCHOR_ATTEMPTED':
      return { ...state, anchorAttempted: true }
    case 'SEAL':
      return { ...state, sealed: true }
    case 'RESET':
      return initialState
  }
}

export const isCaptured = (state: InstallerState, id: ProofStepId): boolean =>
  state.captured.includes(id)

export const capturedCount = (state: InstallerState): number =>
  state.captured.length

export const nextUncapturedIdx = (state: InstallerState): number =>
  PROOF_STEPS.findIndex(p => !state.captured.includes(p.id))

const STORAGE_KEY = 'veritact:hvac-installer-state:v1'

const loadFromStorage = (): InstallerState => {
  if (typeof window === 'undefined') return initialState
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return initialState
    return { ...initialState, ...JSON.parse(raw) as InstallerState }
  } catch {
    return initialState
  }
}

interface ContextValue {
  state: InstallerState
  dispatch: Dispatch<Action>
}

const Ctx = createContext<ContextValue | null>(null)

export const DemoProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [state, dispatch] = useReducer(reducer, undefined, loadFromStorage)

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state])

  return <Ctx.Provider value={{ state, dispatch }}>{children}</Ctx.Provider>
}

export const useDemoStore = (): ContextValue => {
  const v = useContext(Ctx)
  if (!v) throw new Error('useDemoStore called outside DemoProvider')
  return v
}
